import {
  useMemo,
  FunctionComponent,
  KeyboardEvent,
  useState,
  forwardRef,
  PropsWithoutRef,
  Ref,
  useEffect,
  useCallback
} from 'react';

import Text from '../../Text';
import { CALENDAR_TYPES } from '../Input/local';
import {
  getCalendarTypeFromLocale,
  getDateOfISOWeek,
  getWeek,
  parseToDate,
  toUTCDateString
} from '../Input/utils';
import { useConfiguration, useConsolidatedRef, useFocusWithin, useI18n } from '../../../hooks';
import { ForwardProps } from '../../../types';
import { FormControlProps } from '../../FormControl';

import { getDaysOfMonth, getLocalizedDays, getWeeksOfMonth } from './utils';
import {
  StyledColumnLabel,
  ScreenReaderOnly,
  StyledPick,
  StyledDay,
  StyledCalendar,
  StyledWeekGrid,
  StyledWeekLabel,
  StyledWeekNumber
} from './Calendar.styles';

export interface CalendarProps {
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /**
   * Defines default date that will be selected initially when the Calendar is visible.
   */
  selected?: Date;
  /**
   * Defines currently visible month.
   * @default new Date()
   */
  current?: Date;
  /** Defines dates range with start date and end date that can be set by the user. */
  selectedRange?: { startDate: Date | undefined; endDate: Date | undefined };
  /** Defines if days should be highlighted for range selection  */
  showHighlight?: boolean;
  /** Defines if week numbers should be displayed in calendar  */
  showWeekNumber?: boolean;
  /**
   * The mode of the calendar that will be rendered, ie. whether single date or week are selectable.
   * @default "date"
   */
  mode?: 'date' | 'week';
  /** Callback fired when new day is selected. */
  onSelect?: (date: Date) => void;
  /** Callback fired when user focuses new day. */
  onFocus?: (date: Date) => void;
  /** Sets the type of calendar to be used. This will be determined by locale if no value is provided. */
  calendarType?: string;
  /** Defines minimum available value that can be selected by the user. */
  min?: string | number | Date;
  /** Defines maximum available value that can be selected by the user. */
  max?: string | number | Date;
  /** Set the calendar to disabled state, ie. non-interactive. */
  disabled?: boolean;
}

const ariaOpts = { day: 'numeric', month: 'long', year: 'numeric' } as const;

const Calendar: FunctionComponent<CalendarProps & ForwardProps> = forwardRef(
  (
    {
      selected,
      current = selected ? new Date(selected.getTime()) : new Date(),
      selectedRange,
      showHighlight,
      mode = 'date',
      showWeekNumber,
      calendarType: calendar = CALENDAR_TYPES.ISO_8601,
      onSelect,
      onFocus,
      disabled,
      min: minProp,
      max: maxProp
    }: PropsWithoutRef<CalendarProps>,
    ref: Ref<HTMLTableElement>
  ) => {
    const { locale } = useConfiguration();
    const min = minProp ? parseToDate(minProp) : -Infinity;
    const max = maxProp ? parseToDate(maxProp) : Infinity;

    const showWeekLabel = mode === 'week' ? false : showWeekNumber;
    const [focusedDay, setFocusedDay] = useState(current);
    useEffect(() => {
      setFocusedDay(current);
    }, [current.getUTCFullYear(), current.getUTCMonth()]);

    const calendarType = useMemo(
      () => calendar ?? getCalendarTypeFromLocale(locale),
      [locale, calendar]
    );

    const [highlightBoundary, setHighlightBoundary] = useState<Date>();

    const onKeyDown = useCallback(
      (e: KeyboardEvent<HTMLElement>) => {
        if (
          (mode === 'date'
            ? ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
            : ['ArrowUp', 'ArrowDown']
          ).includes(e.key)
        ) {
          e.preventDefault();
          const newFocused = new Date(focusedDay);
          switch (e.key) {
            case 'ArrowUp':
              newFocused.setUTCDate(focusedDay.getUTCDate() - 7);
              break;
            case 'ArrowDown':
              newFocused.setUTCDate(focusedDay.getUTCDate() + 7);
              break;
            case 'ArrowRight':
              newFocused.setUTCDate(focusedDay.getUTCDate() + 1);
              break;
            case 'ArrowLeft':
              newFocused.setUTCDate(focusedDay.getUTCDate() - 1);
              break;
            default:
          }
          if (newFocused < min || newFocused > max) return;
          setFocusedDay(newFocused);
          onFocus?.(newFocused);
          setHighlightBoundary(newFocused);
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          onSelect?.(focusedDay);
        }
      },
      [mode, focusedDay, onFocus, onSelect]
    );

    const containerRef = useConsolidatedRef<HTMLTableElement>(ref);
    const hasFocus = useFocusWithin([containerRef]);

    const today = toUTCDateString(new Date());

    const isDisabled = (day: Date) => {
      return min > day.getTime() || day.getTime() > max;
    };

    const isSelected = (day: Date) => {
      return [
        selected && toUTCDateString(selected),
        selectedRange?.startDate && toUTCDateString(selectedRange?.startDate),
        selectedRange?.endDate && toUTCDateString(selectedRange?.endDate)
      ].includes(toUTCDateString(day));
    };

    const isWeekSelected = (day: Date) => {
      return (
        selected &&
        toUTCDateString(day) ===
          toUTCDateString(getDateOfISOWeek(getWeek(selected, calendarType), selected.getFullYear()))
      );
    };

    const isFocused = (day: Date) => {
      return toUTCDateString(day) === toUTCDateString(focusedDay);
    };

    const isWeekFocused = (day: Date) => {
      return (
        toUTCDateString(day) ===
        toUTCDateString(
          getDateOfISOWeek(getWeek(focusedDay, calendarType), focusedDay.getFullYear())
        )
      );
    };

    const isInRange = (day: Date) => {
      return (
        selectedRange &&
        selectedRange.startDate &&
        selectedRange.endDate &&
        day >= selectedRange.startDate &&
        day <= selectedRange.endDate
      );
    };

    const isHighlighted = (day: Date) => {
      if (!selectedRange || !showHighlight || !highlightBoundary) return false;
      const betweenHighlightAndBoundary = (rangeBoundaryType: 'startDate' | 'endDate') => {
        const rangeBoundary = selectedRange[rangeBoundaryType];
        if (!rangeBoundary) return false;
        return !!(
          rangeBoundary &&
          ((day >= rangeBoundary && day <= highlightBoundary) ||
            (day <= rangeBoundary && day >= highlightBoundary))
        );
      };
      return betweenHighlightAndBoundary('startDate') || betweenHighlightAndBoundary('endDate');
    };

    const days = getDaysOfMonth(current, calendarType ?? CALENDAR_TYPES.ISO_8601).map(day => {
      return (
        <StyledDay
          key={day.getTime()}
          variant='simple'
          icon={false}
          type='button'
          loading={false}
          ref={
            isFocused(day)
              ? (focusedRef: HTMLButtonElement) => {
                  if (hasFocus) focusedRef?.focus();
                }
              : undefined
          }
          tabIndex={isFocused(day) ? 0 : -1}
          disabled={disabled || mode === 'week' || isDisabled(day)}
          current={toUTCDateString(day) === today}
          highlighted={isInRange(day) || isHighlighted(day)}
          aria-selected={isSelected(day) || undefined}
          aria-readonly={day.getUTCMonth() !== current.getUTCMonth() || undefined}
          aria-label={toUTCDateString(day, Intl.DateTimeFormat(locale, ariaOpts))}
          onMouseOver={showHighlight ? () => setHighlightBoundary(day) : undefined}
          onMouseOut={showHighlight ? () => setHighlightBoundary(undefined) : undefined}
          onMouseDown={() => {
            setFocusedDay(day);
            onFocus?.(day);
            onSelect?.(day);
          }}
        >
          {day.getUTCDate()}
        </StyledDay>
      );
    });

    const names = useMemo(() => getLocalizedDays(calendarType, locale), [locale, calendarType]);

    const t = useI18n();

    const weekText = t('week_label_a11y');

    const weeks = getWeeksOfMonth(current, calendarType).map(weekNo => {
      return (
        <>
          <Text as={StyledWeekNumber} key={weekNo} aria-hidden='true'>
            {weekNo.toString()}
          </Text>
          <ScreenReaderOnly>
            {`${weekText} ${weekNo.toString()} ${current.getUTCFullYear().toString()}`}
          </ScreenReaderOnly>
        </>
      );
    });

    const weeksButtons = getWeeksOfMonth(current, calendarType).map(weekNo => {
      const dateOfISOWeek = getDateOfISOWeek(weekNo, current.getUTCFullYear());
      return (
        <StyledPick
          key={weekNo}
          variant='simple'
          icon={false}
          type='button'
          loading={false}
          ref={
            isWeekFocused(dateOfISOWeek)
              ? (focusedRef: HTMLButtonElement) => {
                  if (hasFocus) focusedRef?.focus();
                }
              : undefined
          }
          tabIndex={isWeekFocused(dateOfISOWeek) ? 0 : -1}
          disabled={disabled || mode === 'date' || isDisabled(dateOfISOWeek)}
          aria-selected={isWeekSelected(dateOfISOWeek) || undefined}
          aria-label={`${t('week_label_a11y')} ${weekNo.toString()}, ${current
            .getUTCFullYear()
            .toString()}`}
          onMouseDown={() => {
            setFocusedDay(dateOfISOWeek);
            onFocus?.(dateOfISOWeek);
            onSelect?.(dateOfISOWeek);
          }}
        >
          {`${weekText} `}
          {weekNo.toString()}
        </StyledPick>
      );
    });

    const weekLabel =
      mode === 'date' ? (
        <>
          <Text as={StyledWeekLabel} forwardedAs='abbr' title={weekText} aria-hidden='true'>
            {t('week_text_short')}
          </Text>
          <ScreenReaderOnly>{weekText}</ScreenReaderOnly>
        </>
      ) : (
        <Text as={StyledWeekLabel}>{weekText}</Text>
      );

    const headerLabels = names.map(([name, abbr]) => (
      <th role='columnheader' key={name}>
        <Text as={StyledColumnLabel} forwardedAs='abbr' key={name} title={name} aria-hidden='true'>
          {abbr}
        </Text>
        <ScreenReaderOnly>{name}</ScreenReaderOnly>
      </th>
    ));

    const splitToWeeks = (resultArray: JSX.Element[][], item: JSX.Element, index: number) => {
      const chunkIndex = Math.floor(index / 7);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [] as JSX.Element[];
      }

      resultArray[chunkIndex].push(item);
      return resultArray;
    };

    const cells = days.reduce(splitToWeeks, []).map((week, index) => {
      const rowHeader = mode === 'week' ? weeksButtons[index] : weeks[index];
      return (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={`week-${index}`}>
          {showWeekLabel && <th scope='row'>{rowHeader}</th>}
          {week.map((day, dayIndex) => {
            // eslint-disable-next-line react/no-array-index-key
            return <td key={`day-${dayIndex}`}>{day}</td>;
          })}
        </tr>
      );
    });

    const weeksCells = weeksButtons.map((week, index) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={`week-${index}`}>
          <td>{week}</td>
        </tr>
      );
    });

    const weeksGrid = (
      <StyledWeekGrid onKeyDown={onKeyDown} ref={containerRef}>
        <thead>
          <tr>
            <th>{weekLabel}</th>
          </tr>
        </thead>
        <tbody>{weeksCells}</tbody>
      </StyledWeekGrid>
    );

    return (
      <>
        {mode === 'week' && weeksGrid}
        <StyledCalendar
          ref={mode === 'date' ? containerRef : undefined}
          aria-label={current.toLocaleString(locale, {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC'
          })}
          onKeyDown={onKeyDown}
        >
          <thead>
            <tr>
              {showWeekLabel && <th>{weekLabel}</th>}
              {headerLabels}
            </tr>
          </thead>
          <tbody>{cells}</tbody>
        </StyledCalendar>
        <ScreenReaderOnly aria-live='polite'>{t('calendar_assist')}</ScreenReaderOnly>
      </>
    );
  }
);

export default Calendar;
